import { useTranslation } from 'react-i18next';

import { FacebookIntegrationState } from '@feathr/blackbox';
import { useAccount, useFlags } from '@feathr/extender/state';

export interface IMetaIntegrationStatus {
  /** Whether the Meta integration tile should be disabled due to outage */
  isDisabled: boolean;
  /** Whether the account has an existing Meta integration (not in 'not_connected' state) */
  hasExistingIntegration: boolean;
  /** Whether the account has accepted the custom audience terms of service */
  hasAcceptedCustomAudienceTOS: boolean;
  /** Temporary outage message for disabled integrations */
  disabledMessage: string;
}

/**
 * Hook to determine Meta integration availability status during temporary outage.
 *
 * Disables Meta integration for accounts that:
 * - Do NOT have existing Meta integration AND
 * - Have NOT accepted custom audience terms of service
 *
 * This is a temporary measure to prevent customer frustration during Meta integration outages.
 *
 * @returns Object containing integration status and related flags
 */
export default function useMetaIntegrationStatus(): IMetaIntegrationStatus {
  const account = useAccount();
  const { t } = useTranslation();
  const { metaIntegrationOutage = false } = useFlags();

  // Get Facebook integration from account
  const facebookIntegration = account.get('facebook');

  // Check if account has existing integration (any state except 'not_connected')
  const hasExistingIntegration =
    facebookIntegration && facebookIntegration.state !== FacebookIntegrationState.not_connected;

  // Check if custom audience TOS has been accepted
  const hasAcceptedCustomAudienceTOS =
    facebookIntegration?.tos_accepted?.custom_audience_tos || false;

  // Disable Meta integration for accounts without existing integration AND without TOS acceptance
  const isDisabled =
    metaIntegrationOutage && !hasExistingIntegration && !hasAcceptedCustomAudienceTOS;

  const disabledMessage = t(
    'Meta integration is temporarily unavailable due to a limited outage. Please contact our Support team for any questions.',
  );

  return {
    isDisabled,
    hasExistingIntegration: !!hasExistingIntegration,
    hasAcceptedCustomAudienceTOS,
    disabledMessage,
  };
}
